import { useSelector } from 'react-redux'
import { useQuery } from 'react-query'
import { useAuth0 } from '../../contexts/auth0-context'
import endpoints from '../../API/endpoints'
import callAPI from '../../API/callSecureAPI'

export type Evidence = {
    _id: string
    name: string
    author: string
    abstract: string
    citation: string
    link?: string
    s3Name?: string
    mimeType?: string
    originalFileName?: string
    category?: string
    year?: number
    internal?: boolean
    order?: number
    sharepointFileLink?: Record<string, unknown>
    isSnapshot?: boolean
    snapshotData?: {
        sourceId?: string
        sourceStrategyId?: string
    }
    uniqueStudyIdentifier?: string
    isInternal?: boolean
    subPopulation?: string
    strategyId: string
    organizationId: string
    linkedActivities?: string[]
    linkedValueMessages?: string[]
    primaryValueMessages?: string[]
    uniqueIdentifier?: string
    population?: string
    publicationType?: string
    nickname?: string
    pubmedId?: string
    createdAt?: string
    updatedAt?: string
}

const ONE_MINUTE = 1000 * 60
const FIVE_MINUTES = ONE_MINUTE * 5

export const getEvidenceQueryKey = (strategyId?: string) => ['evidence', 'list', strategyId]

export const getEvidenceByIdQueryKey = (evidenceId: string) => ['evidence', 'byId', evidenceId]

export const useGetEvidenceQuery = () => {
    const { getTokenSilently, isAuthenticated, isLoading } = useAuth0()

    // @ts-expect-error store is not typed properly
    const strategyId = useSelector((state) => state.strategy?._id)

    const getEvidence = async (): Promise<Evidence[] | undefined> => {
        try {
            const token = await getTokenSilently?.({ audience: 'https://atlas.aesara.com' })

            const { url, method } = endpoints.getEvidenceForTable

            const response = await callAPI(url + strategyId, method, token)

            return response.data as Evidence[]
        } catch (e) {
            console.log(e)
        }
    }

    return useQuery<Evidence[] | undefined>(getEvidenceQueryKey(strategyId), getEvidence, {
        enabled: !!strategyId && !!getTokenSilently && !!isAuthenticated && !isLoading,
        cacheTime: FIVE_MINUTES,
        staleTime: FIVE_MINUTES,
        retry: 3,
    })
}

export const useGetEvidenceByIdQuery = (evidenceId?: string) => {
    const { getTokenSilently, isAuthenticated, isLoading } = useAuth0()

    const getEvidenceById = async (): Promise<Evidence | undefined> => {
        if (!evidenceId) return undefined

        const token = await getTokenSilently?.({ audience: 'https://atlas.aesara.com' })

        const { url, method } = endpoints.getEvidenceById
        const urlWithId = url.replace(':id', evidenceId)
        const response = await callAPI(urlWithId, method, token)

        return response.data as Evidence
    }

    return useQuery<Evidence | undefined>(getEvidenceByIdQueryKey(evidenceId || 'none'), getEvidenceById, {
        enabled: !!evidenceId && !!getTokenSilently && !!isAuthenticated && !isLoading,
        cacheTime: FIVE_MINUTES,
        staleTime: FIVE_MINUTES,
        retry: 3,
    })
}
